import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import KpiStrip from "@/components/population/KpiStrip";
import Charts from "@/components/population/Charts";
import SummaryTables from "@/components/population/SummaryTables";
import Roster from "@/components/population/Roster";
import OfficialCensus from "@/components/population/OfficialCensus";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { downloadBlob } from "@/lib/download";
import { toast } from "sonner";
import { BarChart3, Table2, Loader2, FileDown, ClipboardList } from "lucide-react";

export default function Population() {
  const { hasRole } = useAuth();
  const officer = hasRole("officer");
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const load = () => api.get("/population").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const report = async () => {
    setDownloading(true);
    try { await downloadBlob("/population/report.pdf", "IOTA-Galleon-Membership-Dashboard.pdf"); toast.success("Membership report downloaded"); }
    catch { toast.error("Could not generate the report"); } finally { setDownloading(false); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--gold))]" /></div>;
  const { stats } = data;
  const reportDate = new Date(stats.report_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const sourceLabel = data.source === "official" ? "Official census" : "Live roster";
  return (
    <div data-testid="population-page">
      <PageHeader eyebrow="Population data" title="Membership Dashboard" description={`${sourceLabel} · Based on Decknames, Designation, Status and Frigate · ${stats.total} members on record · Report date: ${reportDate}${data.updated_by ? ` · Roster last edited by ${data.updated_by}` : ""}`} />
      <Tabs value={tab} onValueChange={(v) => setParams(v === "dashboard" ? {} : { tab: v })}>
        <TabsList className="glass mb-6 flex-wrap">
          <TabsTrigger value="dashboard" data-testid="population-tab-dashboard"><BarChart3 className="mr-1.5 h-4 w-4" />Dashboard</TabsTrigger>
          <TabsTrigger value="census" data-testid="population-tab-census"><ClipboardList className="mr-1.5 h-4 w-4" />Official census{officer ? " (edit)" : ""}</TabsTrigger>
          <TabsTrigger value="roster" data-testid="population-tab-roster"><Table2 className="mr-1.5 h-4 w-4" />Roster{officer ? " (edit)" : ""}</TabsTrigger>
          {officer && (
            <Button size="sm" onClick={report} disabled={downloading} className="ml-auto bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="population-report-button">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="mr-1.5 h-4 w-4" />Download PDF report</>}
            </Button>
          )}
        </TabsList>
        <TabsContent value="dashboard" className="space-y-6">
          <KpiStrip stats={stats} />
          <Charts stats={stats} />
          <SummaryTables stats={stats} />
        </TabsContent>
        <TabsContent value="census"><OfficialCensus key={data.official.updated_at || "init"} data={data} officer={officer} onSaved={(d) => setData(d)} /></TabsContent>
        <TabsContent value="roster"><Roster data={data} officer={officer} onChanged={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
